import { useState, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { SimpleModal } from '../../../../../../components/simple-modal/simple-modal';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';

import { CardType } from '../../types';
import { useOperationsCardsData } from '../../contexts/operations-cards-data-context';
import { CardOption } from './card-option';
import { messages } from './messages';
import style from './edit-cards-modal.module.less';

type EditCardsModalProps = {
  initialSelectedCards: CardType[];
  onSave: (selectedCards: CardType[]) => void;
  onClose: () => void;
};

export const EditCardsModal = ({ initialSelectedCards, onSave, onClose }: EditCardsModalProps) => {
  const intl = useIntl();
  const [selectedCards, setSelectedCards] = useState<CardType[]>(initialSelectedCards);
  const { fetchAsyncData } = useOperationsCardsData();

  useEffect(() => {
    const allCardTypes = Object.values(CardType);
    void fetchAsyncData(allCardTypes);
  }, [fetchAsyncData]);

  const handleToggle = useCallback((cardType: CardType) => {
    setSelectedCards((prev) => {
      if (prev.includes(cardType)) {
        return prev.filter((t) => t !== cardType);
      }
      if (prev.length < 3) {
        return [...prev, cardType];
      }
      return prev;
    });
  }, []);

  const handleSave = useCallback(() => {
    if (selectedCards.length === 3) {
      onSave(selectedCards);
    }
  }, [selectedCards, onSave]);

  const allCardTypes = Object.values(CardType);

  return (
    <div className={style['modal-wrapper']}>
      <SimpleModal
        actionButtonDataPendoId='edit-cards-save'
        actionMessage={messages.save}
        cancelMessage={messages.cancel}
        headerMessage={messages.modalTitle}
        isActionDisabled={selectedCards.length !== 3}
        onAction={handleSave}
        onCancel={onClose}
        onClose={onClose}
        size='medium'
      >
        <FlexColumn gap={Gap.Space16}>
          <Text textType={TextType.Paragraph} value={intl.formatMessage(messages.modalSubtitle)} />
          <div className={style['cards-grid']}>
            {allCardTypes.map((cardType) => (
              <CardOption
                key={cardType}
                cardType={cardType}
                isDisabled={!selectedCards.includes(cardType) && selectedCards.length >= 3}
                isSelected={selectedCards.includes(cardType)}
                onToggle={() => handleToggle(cardType)}
              />
            ))}
          </div>
        </FlexColumn>
      </SimpleModal>
    </div>
  );
};
